import { useEffect, useState } from "react";
import { Sparkles, TrendingDown, TrendingUp } from "lucide-react";

import { api } from "@/lib/auth";

/**
 * Real-time waitlist conversion strip for the admin CRM.
 *
 * Reads the current ISO week (Australia/Sydney, Mon-Sun) directly from Mongo
 * — no GA4 delay. Each city tile shows this-week count, delta vs last week,
 * and a mini share-of-week bar. Silently hides itself if the API is down so
 * the CRM's core table stays visible.
 */
export function ConversionStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/analytics/waitlist-weekly");
        if (!cancelled) setData(data);
      } catch {
        // Silently hide if the endpoint fails — the strip is a nice-to-have.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !data) return null;

  const total = data.total_this_week ?? 0;
  const delta = data.total_delta_pct;
  return (
    <div
      data-testid="admin-conversion-strip"
      className="mb-6 rounded-2xl border border-gold/40 bg-[#F7F5F2] p-5"
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <span className="text-xs uppercase tracking-[0.24em] text-[#8B6F1F]">
            This week's conversions
          </span>
          <span
            data-testid="conversion-strip-total"
            className="font-serif text-3xl leading-none text-[#2A2724]"
          >
            {total}
          </span>
          <DeltaBadge value={delta} testid="conversion-strip-delta" />
        </div>
        <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[#9a9a9a]">
          Live · from waitlist DB
        </span>
      </div>

      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {(data.cities || []).map((c) => {
          const share = total > 0 ? Math.min(100, Math.round((c.this_week / total) * 100)) : 0;
          return (
            <li
              key={c.city}
              data-testid={`conversion-strip-city-${c.city.toLowerCase()}`}
              className="rounded-xl border border-black/10 bg-white p-3"
            >
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-[#2A2724]">{c.city}</span>
                <DeltaBadge
                  value={c.delta_pct}
                  testid={`conversion-strip-delta-${c.city.toLowerCase()}`}
                  compact
                />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-serif text-xl text-[#2A2724]">{c.this_week}</span>
                <span className="text-[0.65rem] uppercase tracking-[0.18em] text-[#9a9a9a]">
                  vs {c.last_week} last wk
                </span>
              </div>
              <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-black/5">
                <div
                  className="h-full rounded-full bg-gold"
                  style={{ width: `${share}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Small delta badge — green up-arrow for growth, red down-arrow for decline,
 * neutral gray "Flat" pill when the delta is exactly zero, and a gray "NEW"
 * pill when the previous window had no data (delta is null / undefined).
 */
export function DeltaBadge({ value, testid, compact = false }) {
  if (value === null || value === undefined) {
    return (
      <span
        data-testid={testid}
        className={`inline-flex items-center gap-1 rounded-full border border-black/10 bg-[#F7F5F2] text-[#8a847e] ${
          compact ? "px-1.5 py-0.5 text-[0.6rem]" : "px-2 py-0.5 text-[0.65rem]"
        } uppercase tracking-[0.14em]`}
      >
        New
      </span>
    );
  }
  const isFlat = value === 0;
  const up = value > 0;
  const Icon = isFlat ? Sparkles : up ? TrendingUp : TrendingDown;
  const tone = isFlat
    ? "text-[#6B6B6B] bg-[#EDEAE4]"
    : up
      ? "text-[#2f7d3f] bg-[#E9F5EC]"
      : "text-[#a13a3a] bg-[#F8ECEC]";
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full ${tone} ${
        compact ? "px-1.5 py-0.5 text-[0.6rem]" : "px-2 py-0.5 text-[0.7rem]"
      } font-medium tracking-tight`}
    >
      <Icon className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />
      {isFlat ? "Flat" : `${up ? "+" : ""}${value}%`}
    </span>
  );
}
